import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isListingImageFile } from '../utils/imageFileUtils';

export interface SelectedListingImage {
  id: string;
  file: File;
  previewUrl: string;
}

function fileKey(file: File): string {
  return `${file.name}-${file.size}-${file.lastModified}`;
}

/**
 * Manages selected listing image files with blob preview URLs and remove support.
 */
export function useListingImagePreviews(initialFiles: File[] = []) {
  const [images, setImages] = useState<SelectedListingImage[]>(() =>
    initialFiles.filter(isListingImageFile).map((file) => ({
      id: fileKey(file),
      file,
      previewUrl: URL.createObjectURL(file),
    }))
  );
  const imagesRef = useRef(images);
  imagesRef.current = images;

  useEffect(() => {
    return () => {
      imagesRef.current.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    };
  }, []);

  const addFiles = useCallback((fileList: FileList | File[] | null | undefined) => {
    if (!fileList || ('length' in fileList && fileList.length === 0)) return;
    const incoming = Array.from(fileList).filter(isListingImageFile);
    if (incoming.length === 0) return;

    setImages((prev) => {
      const existingKeys = new Set(prev.map((item) => fileKey(item.file)));
      const next = [...prev];
      incoming.forEach((file) => {
        const key = fileKey(file);
        if (existingKeys.has(key)) return;
        existingKeys.add(key);
        next.push({
          id: key,
          file,
          previewUrl: URL.createObjectURL(file),
        });
      });
      return next;
    });
  }, []);

  const removeImage = useCallback((id: string) => {
    setImages((prev) => {
      const target = prev.find((item) => item.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((item) => item.id !== id);
    });
  }, []);

  const reorderImages = useCallback((fromIndex: number, toIndex: number) => {
    setImages((prev) => {
      if (fromIndex < 0 || toIndex < 0 || fromIndex >= prev.length || toIndex >= prev.length) {
        return prev;
      }
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  }, []);

  const clearImages = useCallback(() => {
    setImages((prev) => {
      prev.forEach((item) => URL.revokeObjectURL(item.previewUrl));
      return [];
    });
  }, []);

  const previewItems = useMemo(
    () =>
      images.map((item) => ({
        id: item.id,
        src: item.previewUrl,
        name: item.file.name,
      })),
    [images]
  );

  const files = useMemo(() => images.map((item) => item.file), [images]);

  return {
    images,
    previewItems,
    files,
    addFiles,
    removeImage,
    reorderImages,
    clearImages,
  };
}
